import { makeAutoObservable, runInAction } from 'mobx';

import AuthStore, { type IAuthStore } from '../auth/store';
import MeasurementsStore, { type IMeasurementStore } from '../components/MeasurementsAction/store';
import MediaStore, { type IMediaStore } from '../components/MediaUploader/store';
import {
  FirebaseSessionStore,
  type IFirebaseSessionStore,
} from '../hooks/useFirebaseSessionLogic/store';
import { DataSourceFirebaseStore } from '../hooks/useFirebaseSessionLogic/useDataSourceFirebase/store';
import { type IDataSourceFirebaseStore } from '../hooks/useFirebaseSessionLogic/useDataSourceFirebase/types';
import RtmStoreSingleton, { type IRtmStore } from '../hooks/useRTM/store';
import { AdminDashboardStore } from '../Pages/Admin/AdminDashboard/AdminDashboard.store';
import { type IAdminDashboardStore } from '../Pages/Admin/AdminDashboard/AdminDashboard.types';
import ClinicManagementStore, { type IClinicManagementStore } from '../Pages/ClinicView/store';
import CategoriesStore, {
  type ICategoriesStore,
  type ICategory,
} from '../Pages/Content/Categories/store';
import EquipmentStore, { type IEquipment, type IEquipmentStore } from '../Pages/Equipment/store';
import HomepageStore, { type IHomepageStore } from '../Pages/Homepage/store';
import MeetingsStore, { type IMeetingsStore } from '../Pages/Meetings/store';
import PatientDetailsStore, { type IPatientDetailsStore } from '../Pages/PatientDetailsPages/store';
import PatientsStore, { type IPatientsStore } from '../Pages/Patients/store';
import SinglePatientStore, {
  type ISinglePatientStore,
} from '../Pages/SinglePatientDetails/Service/store';
import AppStore, { type IAppStore, type IGaitParameterInfo } from '../Pages/store';
import { CareLogStore } from '../services/careLogService/store';
import { type ICareLogStore } from '../services/careLogService/types';
import { type IIntakeDataStore, IntakeDataStore } from '../services/intakeDataService/store';
import { type ILoginStore, LoginStore } from '../services/loginService/store';
import { PatientNotesStore } from '../services/patientNoteService/stores';
import { type IPatientNotesStore } from '../services/patientNoteService/types';
import {
  type IPatientProfileStore,
  PatientProfileStore,
} from '../services/patientProfileService/store';
import {
  type IPatientsServiceStore,
  PatientsServiceStore,
} from '../services/patientsService/store';
import { RollupStore } from '../services/rollupServiceV2/store';
import { type IRollupStore } from '../services/rollupServiceV2/types';
import { type ISignUpStore, SignUpStore } from '../services/signUpService/store';
import { type IWebTutorialStore, WebTutorialStore } from '../services/webTutorialService/store';
import { type IFraFilters } from './appDataService';
import DownloadsPanelStore, { type IDownloadsPanelStore } from './downloads-panel-store';
import ScrollStore, { type IScrollStore } from './scroll-store';

export interface AppData {
  gaitParams: IGaitParameterInfo[];
  equipments: IEquipment[];
  categories: ICategory[];
  medicalDevices: string[];
  fraFilters?: IFraFilters[];
  staleThresholdDays?: number;
}

export interface IRootStore {
  load(data: AppData): void;

  appStore: IAppStore;
  authStore: IAuthStore;
  patientsStore: IPatientsStore;
  homepageStore: IHomepageStore;
  patientsServiceStore: IPatientsServiceStore;
  patientDetailsStore: IPatientDetailsStore;
  mediaStore: IMediaStore;
  meetingsStore: IMeetingsStore;
  measurementsStore: IMeasurementStore;
  equipmentStore: IEquipmentStore;
  rtmStore: IRtmStore;
  clinicManagementStore: IClinicManagementStore;
  categoriesStore: ICategoriesStore;
  singlePatientStore: ISinglePatientStore;
  patientProfileStore: IPatientProfileStore;
  loginStore: ILoginStore;
  signUpStore: ISignUpStore;
  webTutorialStore: IWebTutorialStore;
  intakeDataStore: IIntakeDataStore;
  // @todo: fix typo
  measurmentUnit: IMeasurementStore;
  careLogStore: ICareLogStore;
  patientNotesStore: IPatientNotesStore;
  dataSourceFirebaseStore: IDataSourceFirebaseStore;
  firebaseSessionStore: IFirebaseSessionStore;
  tabUUID: string;
  scrollStore: IScrollStore;
  adminDashboardStore: IAdminDashboardStore;
  rollupStore: IRollupStore;
  downloadsPanelStore: IDownloadsPanelStore;
}

export default class RootStore implements IRootStore {
  authStore = new AuthStore() as IAuthStore;
  appStore = new AppStore() as IAppStore;
  patientsStore = new PatientsStore(this.appStore, this.authStore) as IPatientsStore;
  clinicManagementStore = new ClinicManagementStore(this.appStore) as IClinicManagementStore;
  patientDetailsStore = new PatientDetailsStore(
    this.appStore,
    this.patientsStore,
  ) as IPatientDetailsStore;
  mediaStore = new MediaStore() as IMediaStore;
  meetingsStore = new MeetingsStore() as IMeetingsStore;
  measurementsStore = new MeasurementsStore() as IMeasurementStore;
  equipmentStore = new EquipmentStore() as IEquipmentStore;
  dataSourceFirebaseStore = new DataSourceFirebaseStore();
  firebaseSessionStore = new FirebaseSessionStore(this.dataSourceFirebaseStore);
  private rtmStoreV1 = RtmStoreSingleton.getInstance() as IRtmStore;
  categoriesStore = new CategoriesStore() as ICategoriesStore;
  patientsServiceStore = new PatientsServiceStore(this.appStore);
  homepageStore = new HomepageStore();
  singlePatientStore = new SinglePatientStore();

  patientProfileStore = new PatientProfileStore();

  loginStore = new LoginStore();
  signUpStore = new SignUpStore();

  webTutorialStore = new WebTutorialStore();

  intakeDataStore = new IntakeDataStore();
  measurmentUnit = new MeasurementsStore() as IMeasurementStore;

  careLogStore = new CareLogStore();

  patientNotesStore = new PatientNotesStore();
  scrollStore = new ScrollStore();
  adminDashboardStore = new AdminDashboardStore();
  rollupStore = new RollupStore();
  downloadsPanelStore = new DownloadsPanelStore();
  _tabUUID = '';

  constructor() {
    makeAutoObservable(this);
  }

  load(data: AppData) {
    runInAction(() => {
      this.categoriesStore.categories = data?.categories ?? [];
      this.equipmentStore.equipment = data?.equipments ?? [];
      this.appStore.gaitParams = data?.gaitParams ?? [];
    });
  }

  get tabUUID() {
    return this._tabUUID;
  }
  set tabUUID(newUuid: string) {
    if (this._tabUUID) return;
    this._tabUUID = newUuid;
  }

  get rtmStore(): IRtmStore {
    return this.rtmStoreV1;
  }
}
